import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import DashboardLayout from './layout/DashboardLayout';
import CategoryPage from './CategoryPage';
import BankPage from './BankPage';
import BudgetPage from './BudgetPage';
import LenderPage from './LenderPage';
import BorrowerPage from './BorrowerPage';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend, Area, AreaChart, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { getBankTransactions } from '../api/bankTransactionApi';
import { getBanks } from '../api/bankApi';
import { getBudgets } from '../api/budgetApi';
import { getCategories } from '../api/categoryApi';
import { getLenders } from '../api/lenderApi';
import { getBorrowers } from '../api/borrowerApi';
import { getLenderTransactionSummary } from '../api/lenderTransactionApi';
import { getBorrowerTransactionSummary } from '../api/borrowerTransactionApi';

// Shared layout for all dashboard views
const Content = styled.section`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

// Overview page UI
const WelcomeCard = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.08);
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
`;

const MetricCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #f1f5f9;
`;

const MetricLabel = styled.span`
  font-size: 13px;
  color: #94a3b8;
`;

const MetricValue = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
`;

const MetricDelta = styled.span`
  font-size: 13px;
  color: ${(props) => (props.$positive ? '#16a34a' : '#dc2626')};
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.4fr);
  gap: 20px;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ChartCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 20px 20px 16px;
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChartTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  color: #0f172a;
`;

const ChartSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #64748b;
`;

const SmallChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
`;

const InlineStatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
`;

const InlineStat = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-size: 12px;
  color: #64748b;

  strong {
    font-size: 13px;
    color: #0f172a;
    font-weight: 600;
  }
`;

const ActivityFeed = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 24px;
  border: 1px solid #f1f5f9;
`;

const FeedItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const FeedTitle = styled.span`
  font-weight: 600;
  color: #0f172a;
`;

const FeedMeta = styled.span`
  font-size: 13px;
  color: #94a3b8;
`;

const EmptyState = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 60px 32px;
  border: 1px dashed #cbd5f5;
  text-align: center;
  color: #475467;
  font-size: 15px;
`;

// Icons used in the sidebar
const sidebarIconProps = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
};

const OverviewIcon = () => (
  <svg {...sidebarIconProps}>
    <rect x="4" y="4" width="7" height="7" rx="1.6" fill="none" />
    <rect x="13" y="4" width="7" height="7" rx="1.6" fill="none" />
    <rect x="4" y="13" width="7" height="7" rx="1.6" fill="none" />
    <rect x="13" y="13" width="7" height="7" rx="1.6" fill="none" />
  </svg>
);

const BudgetIcon = () => (
  <svg {...sidebarIconProps}>
    <path d="M4 7h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
    <path d="M16 11h4v4h-4a2 2 0 0 1 0-4z" />
    <path d="M8 7V5a2 2 0 0 1 2-2h6" />
  </svg>
);

const BankIcon = () => (
  <svg {...sidebarIconProps}>
    <path d="M5 9V7l7-4 7 4v2" />
    <path d="M3 9h18" />
    <path d="M5 21h14" />
    <path d="M6 9v8" />
    <path d="M10 9v8" />
    <path d="M14 9v8" />
    <path d="M18 9v8" />
  </svg>
);

const CategoryIcon = () => (
  <svg {...sidebarIconProps}>
    <path d="M4 8v5.59a2 2 0 0 0 0.59 1.41l6.41 6.41a2 2 0 0 0 2.82 0l6.59-6.59a2 2 0 0 0 0-2.82L13.41 6H8a4 4 0 0 0-4 4z" />
    <circle cx="10" cy="10" r="1.2" />
  </svg>
);

const LenderIcon = () => (
  <svg {...sidebarIconProps}>
    <circle cx="8" cy="8" r="3" />
    <path d="M4 16h7a4 4 0 0 1 4 4" />
    <path d="M13 5l5 5-5 5" />
    <path d="M13 10h5" />
  </svg>
);

const BorrowerIcon = () => (
  <svg {...sidebarIconProps}>
    <rect x="4" y="11" width="16" height="8" rx="2" />
    <path d="M12 5v8" />
    <path d="M9 8l3-3 3 3" />
  </svg>
);

// Individual "pages" rendered inside the dashboard
const OverviewPage = ({ firstName, metrics, activity }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [cashflowSeries, setCashflowSeries] = useState([]);
  const [balanceSeries, setBalanceSeries] = useState([]);
  const [totals, setTotals] = useState({ income: 0, expenses: 0, savings: 0, balance: 0 });
  const [bankBalances, setBankBalances] = useState([]);
  const [expenseCategorySeries, setExpenseCategorySeries] = useState([]);
  const [incomeCategorySeries, setIncomeCategorySeries] = useState([]);
  const [lenderPieSeries, setLenderPieSeries] = useState([]);
  const [borrowerPieSeries, setBorrowerPieSeries] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const [banksRes, txRes, budgetsRes, categoriesRes, lendersRes, borrowersRes, lenderSummaryRes, borrowerSummaryRes] = await Promise.all([
          getBanks(),
          getBankTransactions(),
          getBudgets(),
          getCategories(),
          getLenders(),
          getBorrowers(),
          getLenderTransactionSummary(),
          getBorrowerTransactionSummary()
        ]);

        const banks = banksRes?.data?.data || [];
        const transactions = txRes?.data?.data || [];
        const budgets = budgetsRes?.data?.data || [];
        const categories = categoriesRes?.data?.data || [];
        const lenders = lendersRes?.data?.data || [];
        const borrowers = borrowersRes?.data?.data || [];
        const lenderSummaryRows = lenderSummaryRes?.data?.data || [];
        const borrowerSummaryRows = borrowerSummaryRes?.data?.data || [];

        const totalBeginning = banks.reduce((sum, bank) => sum + Number(bank.beginningBalance || 0), 0);

        const now = new Date();
        const startDate = new Date();
        startDate.setDate(now.getDate() - 29);
        const balanceStartDate = new Date();
        balanceStartDate.setDate(now.getDate() - 349);
        const categoryWindowStart = new Date();
        categoryWindowStart.setDate(now.getDate() - 359);

        const dateKey = (d) => d.toISOString().slice(0, 10);

        const dailyMap = new Map();

        const parsedTransactions = transactions.map((tx) => {
          const d = new Date(tx.date || tx.transaction_date || tx.createdAt);
          return {
            ...tx,
            dateObj: d,
            dateKey: dateKey(d),
            amount: Number(tx.amount || 0),
            status: (tx.status || 'deposit').toLowerCase()
          };
        });

        // totals across all time for overall bank balance
        let totalDeposits = 0;
        let totalWithdraws = 0;

        parsedTransactions.forEach((tx) => {
          if (tx.status === 'withdraw') {
            totalWithdraws += tx.amount;
          } else if (tx.status === 'deposit') {
            totalDeposits += tx.amount;
          }
        });

        const currentBalance = totalBeginning + totalDeposits - totalWithdraws;

        // bank-wise current balances
        const bankBalanceMap = new Map();

        banks.forEach((bank) => {
          if (bank.id == null) return;
          const begin = Number(bank.beginningBalance || 0) || 0;
          bankBalanceMap.set(bank.id, begin);
        });

        parsedTransactions.forEach((tx) => {
          const bankId = tx.bankAccountId;
          if (!bankBalanceMap.has(bankId)) return;

          const current = bankBalanceMap.get(bankId) || 0;

          if (tx.status === 'withdraw') {
            bankBalanceMap.set(bankId, current - tx.amount);
          } else if (tx.status === 'deposit') {
            bankBalanceMap.set(bankId, current + tx.amount);
          }
        });

        // 30-day window buckets (for income/expense chart)
        for (let i = 0; i < 30; i += 1) {
          const d = new Date(startDate);
          d.setDate(startDate.getDate() + i);
          const key = dateKey(d);
          dailyMap.set(key, {
            date: key.slice(5),
            income: 0,
            expenses: 0,
            savings: 0,
            net: 0
          });
        }

        // Map category name -> Income / Expense type using category.type
        const categoryTypeMap = new Map();
        categories.forEach((cat) => {
          const name = String(cat.name || '').trim();
          if (!name) return;
          const rawType = String(cat.type || '').trim();
          const normalized = rawType === 'Income' ? 'Income' : 'Expense';
          categoryTypeMap.set(name, normalized);
        });

        // Apply budgets in the last 30 days into income/expenses buckets
        budgets.forEach((entry) => {
          if (!entry || !entry.date) return;
          const d = new Date(entry.date);
          if (Number.isNaN(d.getTime())) return;
          if (d < startDate || d > now) return;

          const key = dateKey(d);
          const bucket = dailyMap.get(key);
          if (!bucket) return;

          const amount = Number(entry.amount || 0);
          if (!Number.isFinite(amount) || amount === 0) return;

          const categoryName = String(entry.category || '').trim();
          const categoryType = categoryTypeMap.get(categoryName) || 'Expense';

          if (categoryType === 'Income') {
            bucket.income += amount;
          } else {
            bucket.expenses += amount;
          }
        });

        // Also treat non-cash bank deposits as income within the same 30-day window
        parsedTransactions.forEach((tx) => {
          if (!tx.dateObj || Number.isNaN(tx.dateObj.getTime())) return;
          if (tx.dateObj < startDate || tx.dateObj > now) return;
          if (tx.status !== 'deposit') return;
          if (tx.isFromCash) return; // ignore deposits that are just moving cash into the bank

          const key = tx.dateKey;
          const bucket = dailyMap.get(key);
          if (!bucket) return;

          bucket.income += tx.amount;
        });

        // Aggregate category totals for last 360 days (separate income vs expenses)
        const expenseTotals = new Map();
        const incomeTotals = new Map();

        // From budgets (planned/recorded income & expenses)
        budgets.forEach((entry) => {
          if (!entry || !entry.date) return;
          const d = new Date(entry.date);
          if (Number.isNaN(d.getTime())) return;
          if (d < categoryWindowStart || d > now) return;

          const amount = Number(entry.amount || 0);
          if (!Number.isFinite(amount) || amount === 0) return;

          const categoryName = String(entry.category || '').trim() || 'Uncategorised';
          const categoryType = categoryTypeMap.get(categoryName) || 'Expense';

          if (categoryType === 'Income') {
            incomeTotals.set(categoryName, (incomeTotals.get(categoryName) || 0) + amount);
          } else {
            expenseTotals.set(categoryName, (expenseTotals.get(categoryName) || 0) + amount);
          }
        });

        // From bank transactions: treat non-cash deposits as an additional income category
        const bankDepositIncomeCategory = 'Bank deposits (non-cash)';

        parsedTransactions.forEach((tx) => {
          if (!tx.dateObj || Number.isNaN(tx.dateObj.getTime())) return;
          if (tx.dateObj < categoryWindowStart || tx.dateObj > now) return;
          if (tx.status !== 'deposit') return;
          if (tx.isFromCash) return; // ignore deposits that are just moving cash into the bank

          const amount = Number(tx.amount || 0);
          if (!Number.isFinite(amount) || amount === 0) return;

          incomeTotals.set(
            bankDepositIncomeCategory,
            (incomeTotals.get(bankDepositIncomeCategory) || 0) + amount
          );
        });

        const orderedKeys = Array.from(dailyMap.keys()).sort();
        const cfSeries = orderedKeys.map((key) => {
          const bucket = dailyMap.get(key);
          const dayNet = bucket.income - bucket.expenses;
          return {
            ...bucket,
            net: dayNet,
            savings: Math.max(dayNet, 0)
          };
        });

        const totalsLast30 = cfSeries.reduce(
          (acc, day) => {
            acc.income += day.income;
            acc.expenses += day.expenses;
            return acc;
          },
          { income: 0, expenses: 0 }
        );

        const netSavings = totalsLast30.income - totalsLast30.expenses;

        setCashflowSeries(cfSeries);
        setTotals({ income: totalsLast30.income, expenses: totalsLast30.expenses, savings: netSavings, balance: currentBalance });

        const perBank = banks.map((bank) => {
          const raw = bankBalanceMap.get(bank.id);
          const fallback = Number(bank.beginningBalance || 0) || 0;
          return {
            name: bank.name || `Account ${bank.id}`,
            balance: Number.isFinite(raw) ? raw : fallback
          };
        });

        setBankBalances(perBank);

        const expenseSeries = Array.from(expenseTotals.entries())
          .map(([name, amount]) => ({ name, amount }))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 12);

        const incomeSeries = Array.from(incomeTotals.entries())
          .map(([name, amount]) => ({ name, amount }))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 12);

        setExpenseCategorySeries(expenseSeries);
        setIncomeCategorySeries(incomeSeries);

        // Build per-lender and per-borrower exposure for pie charts
        const lenderPaidTotals = new Map();
        lenderSummaryRows.forEach((row) => {
          if (row.lenderId == null) return;
          const value = row.netPaid !== undefined ? row.netPaid : row.totalPaid;
          lenderPaidTotals.set(row.lenderId, Number(value) || 0);
        });

        const borrowerPaidTotals = new Map();
        borrowerSummaryRows.forEach((row) => {
          if (row.borrowerId == null) return;
          const value = row.netGiven !== undefined ? row.netGiven : row.totalGiven;
          borrowerPaidTotals.set(row.borrowerId, Number(value) || 0);
        });

        const lenderBalances = lenders.map((lender) => {
          const beginning = Number(lender.beginningAmount ?? lender.amount ?? 0) || 0;
          const net = lenderPaidTotals.get(lender.id) || 0; // receive - pay
          return {
            id: lender.id,
            name: lender.name || `Lender ${lender.id}`,
            value: Math.max(beginning + net, 0)
          };
        }).filter((entry) => entry.value > 0);

        const borrowerBalances = borrowers.map((borrower) => {
          const beginning = Number(borrower.beginningAmount ?? borrower.amount ?? 0) || 0;
          const net = borrowerPaidTotals.get(borrower.id) || 0; // pay - receive
          return {
            id: borrower.id,
            name: borrower.name || `Borrower ${borrower.id}`,
            value: Math.max(beginning + net, 0)
          };
        }).filter((entry) => entry.value > 0);

        setLenderPieSeries(lenderBalances);
        setBorrowerPieSeries(borrowerBalances);

        // Build 350-day overall bank balance series from transactions
        const balanceDailyMap = new Map();
        const daysWindow = 350;

        for (let i = 0; i < daysWindow; i += 1) {
          const d = new Date(balanceStartDate);
          d.setDate(balanceStartDate.getDate() + i);
          const key = dateKey(d);
          balanceDailyMap.set(key, {
            date: key.slice(5),
            net: 0
          });
        }

        // baseline: beginning balances plus all transactions before window
        let baselineBalance = totalBeginning;

        parsedTransactions.forEach((tx) => {
          if (!tx.dateObj || Number.isNaN(tx.dateObj.getTime())) return;

          if (tx.dateObj < balanceStartDate) {
            if (tx.status === 'withdraw') {
              baselineBalance -= tx.amount;
            } else if (tx.status === 'deposit') {
              baselineBalance += tx.amount;
            }
            return;
          }

          if (tx.dateObj > now) return;

          const key = tx.dateKey;
          const bucket = balanceDailyMap.get(key);
          if (!bucket) return;

          if (tx.status === 'withdraw') {
            bucket.net -= tx.amount;
          } else if (tx.status === 'deposit') {
            bucket.net += tx.amount;
          }
        });

        const balanceKeys = Array.from(balanceDailyMap.keys()).sort();
        const balSeries = [];
        let running = baselineBalance;

        balanceKeys.forEach((key) => {
          const bucket = balanceDailyMap.get(key);
          running += bucket.net;
          balSeries.push({ date: bucket.date, balance: running });
        });

        setBalanceSeries(balSeries);
      } catch (error) {
        console.error('Failed to load overview chart data', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value) => {
    if (!Number.isFinite(value)) return 'LKR 0';
    const rounded = Math.round(value);
    const formatted = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0
    }).format(rounded);
    return `LKR ${formatted}`;
  };

  const enhancedMetrics = [
    {
      label: 'Bank balance',
      value: formatCurrency(totals.balance),
      delta: totals.savings >= 0 ? 'On track to grow' : 'Spending above income',
      positive: totals.savings >= 0
    },
    {
      label: 'Income (30 days)',
      value: formatCurrency(totals.income),
      delta: 'Money coming in',
      positive: true
    },
    {
      label: 'Expenses (30 days)',
      value: formatCurrency(totals.expenses),
      delta: totals.expenses > totals.income ? 'Spending above income' : 'Below income – keep it up',
      positive: totals.expenses <= totals.income
    },
    {
      label: 'Balance (30 days)',
      value: formatCurrency(totals.savings),
      delta: totals.savings > 0 ? 'Net positive cash flow' : 'No savings this period',
      positive: totals.savings > 0
    }
  ];

  return (
    <>
      <MetricsGrid>
        {enhancedMetrics.map((metric) => (
          <MetricCard key={metric.label}>
            <MetricLabel>{metric.label}</MetricLabel>
            <MetricValue>{metric.value}</MetricValue>
            <MetricDelta $positive={metric.positive !== false}>{metric.delta}</MetricDelta>
          </MetricCard>
        ))}
      </MetricsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Last 30 days: income vs expenses</ChartTitle>
          <ChartSubtitle>
            Aim to keep the green income line above the red expenses line to grow your savings.
          </ChartSubtitle>
          <InlineStatsRow>
            <InlineStat>
              <span>Income:</span>
              <strong>{formatCurrency(totals.income)}</strong>
            </InlineStat>
            <InlineStat>
              <span>Expenses:</span>
              <strong>{formatCurrency(totals.expenses)}</strong>
            </InlineStat>
            <InlineStat>
              <span>Balance:</span>
              <strong
                style={{
                  color: totals.income - totals.expenses >= 0 ? '#16a34a' : '#dc2626'
                }}
              >
                {formatCurrency(totals.income - totals.expenses)}
              </strong>
            </InlineStat>
          </InlineStatsRow>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={cashflowSeries} margin={{ top: 12, right: 8, left: -8, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
                <Legend />
                <Line type="monotone" dataKey="income" name="Income" stroke="#16a34a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#dc2626" strokeWidth={2} dot={false} />
                <Line
                  type="monotone"
                  dataKey="net"
                  name="Net savings"
                  stroke="#0ea5e9"
                  strokeDasharray="4 4"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Bank-wise current balances</ChartTitle>
          <ChartSubtitle>
            See which accounts currently hold most of your money.
          </ChartSubtitle>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={bankBalances} margin={{ top: 12, right: 8, left: -16, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
                <Legend />
                <Bar dataKey="balance" name="Balance" fill="#16a34a" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </ChartsGrid>

      <ChartCard>
        <ChartTitle>Bank balance (last 350 days)</ChartTitle>
        <ChartSubtitle>
          Each move in the line reflects deposits and withdrawals over the past 350 days.
        </ChartSubtitle>
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={balanceSeries} margin={{ top: 12, right: 8, left: -8, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
              />
              <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
              <Legend />
              <Line
                type="monotone"
                dataKey="balance"
                name="Bank balance"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Expenses by category (last 360 days)</ChartTitle>
          <ChartSubtitle>
            Your highest spending categories over the past year.
          </ChartSubtitle>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={expenseCategorySeries} layout="vertical" margin={{ top: 12, right: 16, left: 40, bottom: 0 }}>
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
                <Legend />
                <Bar dataKey="amount" name="Expenses" fill="#dc2626" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Income by category (last 360 days)</ChartTitle>
          <ChartSubtitle>
            Where your income is coming from over the past year.
          </ChartSubtitle>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={incomeCategorySeries} layout="vertical" margin={{ top: 12, right: 16, left: 40, bottom: 0 }}>
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
                <Legend />
                <Bar dataKey="amount" name="Income" fill="#16a34a" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </ChartsGrid>

      {(lenderPieSeries.length > 0 || borrowerPieSeries.length > 0) && (
        <SmallChartsGrid>
          {lenderPieSeries.length > 0 && (
            <ChartCard>
              <ChartTitle>Lender balances</ChartTitle>
              <ChartSubtitle>Total exposure split by each lender.</ChartSubtitle>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={lenderPieSeries}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {lenderPieSeries.map((entry, index) => {
                        const colors = ['#0ea5e9', '#22c55e', '#6366f1', '#14b8a6', '#f97316'];
                        return <Cell key={entry.id ?? entry.name} fill={colors[index % colors.length]} />;
                      })}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          )}

          {borrowerPieSeries.length > 0 && (
            <ChartCard>
              <ChartTitle>Borrower balances</ChartTitle>
              <ChartSubtitle>Outstanding amounts split by each borrower.</ChartSubtitle>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={borrowerPieSeries}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {borrowerPieSeries.map((entry, index) => {
                        const colors = ['#f97316', '#facc15', '#fb7185', '#6366f1', '#22c55e'];
                        return <Cell key={entry.id ?? entry.name} fill={colors[index % colors.length]} />;
                      })}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(Number(value || 0))} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          )}
        </SmallChartsGrid>
      )}
    </>
  );
};

const PlaceholderPage = ({ label }) => (
  <EmptyState>
    {label} page is coming soon.
  </EmptyState>
);

// Main Dashboard component that switches between pages
const navConfig = [
  { label: 'Overview', Icon: OverviewIcon, slug: '' },
  { label: 'Budget', Icon: BudgetIcon, slug: 'budget' },
  { label: 'Bank', Icon: BankIcon, slug: 'bank' },
  { label: 'Lender', Icon: LenderIcon, slug: 'lender' },
  { label: 'Borrower', Icon: BorrowerIcon, slug: 'borrower' },
  { label: 'Category', Icon: CategoryIcon, slug: 'category' }
];

const Dashboard = ({ admin, onLogout, onViewProfile, avatar }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const activeSlug = useMemo(() => {
    if (!location.pathname.startsWith('/dashboard')) {
      return '';
    }

    const remainder = location.pathname.replace('/dashboard', '').replace(/^\//, '');
    return remainder.split('/')[0] || '';
  }, [location.pathname]);

  const navItems = useMemo(() => {
    return navConfig.map((item) => ({
      ...item,
      active: item.slug === activeSlug,
      onClick: () => navigate(item.slug ? `/dashboard/${item.slug}` : '/dashboard')
    }));
  }, [activeSlug, navigate]);

  const activeNavItem = navItems.find((item) => item.active) || navItems[0];

  const metrics = [
    { label: 'Total balance (LKR)', value: 'Rs 0', delta: '' },
    { label: 'Active Clients', value: '214', delta: '+2.1%' },
    { label: 'Risk Alerts', value: '5', delta: '-1 this week', positive: true },
    { label: 'Avg. ROI', value: '11.8%', delta: '+0.4%' }
  ];

  const activity = [
    { title: 'Portfolio rebalanced', meta: '2m ago · Alpha Growth Fund' },
    { title: 'New compliance alert', meta: '12m ago · Policy breach' },
    { title: 'Client onboarding complete', meta: '1h ago · Westwood Capital' }
  ];

  const fullName = admin?.full_name?.trim() || admin?.username || 'Admin';
  const firstName = fullName.split(' ')[0] || 'Admin';

  const handleViewProfile = () => {
    if (onViewProfile) {
      onViewProfile();
      return;
    }

    Swal.fire({
      title: 'Profile',
      text: fullName,
      icon: 'info',
      confirmButtonColor: '#111322'
    });
  };

  const handleLogoutClick = () => {
    Swal.fire({
      title: 'Sign out?',
      text: 'Your secure session will end.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111322',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Log out'
    }).then((result) => {
      if (result.isConfirmed && onLogout) {
        onLogout();
        Swal.fire({
          title: 'Signed out',
          text: 'Come back soon.',
          icon: 'success',
          timer: 1200,
          showConfirmButton: false
        });
      }
    });
  };

  const renderPage = () => {
    if (activeSlug === '' || activeSlug === undefined) {
      return <OverviewPage firstName={firstName} metrics={metrics} activity={activity} />;
    }

    if (activeSlug === 'budget') {
      return <BudgetPage />;
    }

    if (activeSlug === 'bank') {
      return <BankPage />;
    }

    if (activeSlug === 'lender') {
      return <LenderPage />;
    }

    if (activeSlug === 'borrower') {
      return <BorrowerPage />;
    }

    if (activeSlug === 'category') {
      return <CategoryPage />;
    }

    const label = activeSlug.charAt(0).toUpperCase() + activeSlug.slice(1);
    return <PlaceholderPage label={label} />;
  };

  return (
    <DashboardLayout
      admin={admin}
      avatar={avatar}
      navItems={navItems}
      onViewProfile={handleViewProfile}
      onLogout={handleLogoutClick}
      pageTitle={activeNavItem?.label || 'Overview'}
    >
      <Content>{renderPage()}</Content>
    </DashboardLayout>
  );
};

export default Dashboard;
